import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

function DistrictSelect({ district, setDistrict }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const districts = {
    "Ate": { latitude: -12.037, longitude: -76.955 },
    "Barranco": { latitude: -12.145, longitude: -77.021 },
    "Breña": { latitude: -12.063, longitude: -77.049 },
    "Carabayllo": { latitude: -11.884, longitude: -77.038 },
    "Chorrillos": { latitude: -12.178, longitude: -77.001 },
    "Comas": { latitude: -11.936, longitude: -77.060 },
    "El Agustino": { latitude: -12.047, longitude: -76.997 },
    "Independencia": { latitude: -11.982, longitude: -77.049 },
    "Jesús María": { latitude: -12.076, longitude: -77.049 },
    "La Molina": { latitude: -12.073, longitude: -76.947 },
    "La Victoria": { latitude: -12.071, longitude: -77.013 },
    "Lince": { latitude: -12.087, longitude: -77.035 },
    "Los Olivos": { latitude: -11.981, longitude: -77.067 },
    "Magdalena del Mar": { latitude: -12.090, longitude: -77.069 },
    "Miraflores": { latitude: -12.120, longitude: -77.030 },
    "Pueblo Libre": { latitude: -12.078, longitude: -77.073 },
    "Puente Piedra": { latitude: -11.872, longitude: -77.069 },
    "Rimac": { latitude: -12.026, longitude: -77.026 },
    "San Borja": { latitude: -12.101, longitude: -77.001 },
    "San Isidro": { latitude: -12.096, longitude: -77.036 },
    "San Juan de Lurigancho": { latitude: -12.006, longitude: -76.988 },
    "San Juan de Miraflores": { latitude: -12.152, longitude: -76.963 },
    "San Luis": { latitude: -12.073, longitude: -77.000 },
    "San Martin de Porres": { latitude: -12.015, longitude: -77.059 },
    "San Miguel": { latitude: -12.080, longitude: -77.096 },
    "Santa Anita": { latitude: -12.052, longitude: -76.971 },
    "Santiago de Surco": { latitude: -12.140, longitude: -76.995 },
    "Surquillo": { latitude: -12.116, longitude: -77.022 },
    "Villa El Salvador": { latitude: -12.193, longitude: -76.949 },
    "Villa Maria del Triunfo": { latitude: -12.161, longitude: -76.944 },
    "Cercado de Lima": { latitude: -12.046, longitude: -77.031 },
    "Callao": { latitude: -12.061, longitude: -77.151 },
    "Bellavista": { latitude: -12.072, longitude: -77.122 },
    "Carmen de La Legua Reynoso": { latitude: -12.048, longitude: -77.096 },
    "La Perla": { latitude: -12.076, longitude: -77.116 },
    "La Punta": { latitude: -12.075, longitude: -77.167 },
    "Ventanilla": { latitude: -11.878, longitude: -77.135 },
  };

  const districtNames = Object.keys(districts).sort();

  const filteredDistricts = districtNames.filter((d) =>
    d.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    function checkPosition() {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        
        // If less than 300px below, open upward
        setOpenUpward(spaceBelow < 300 && spaceAbove > spaceBelow);
      }
    }

    if (isOpen) {
      checkPosition();
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  function handleSelect(districtName) {
    setDistrict(districtName);
    setIsOpen(false);
    setSearchTerm('');
  }

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] transition-all duration-200"
      >
        <span className={`text-sm font-montserrat ${district ? 'text-slate-900' : 'text-slate-400'}`}>
          {district || 'Selecciona un distrito'}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div 
          className={`absolute z-50 w-full bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden ${
            openUpward ? 'bottom-full mb-2' : 'top-full mt-2'
          }`}
        >
          {/* Search Input */}
          <div className="p-2 border-b border-slate-200">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar distrito..."
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] font-montserrat"
              autoFocus
            />
          </div>

          {/* District List */}
          <div className="max-h-60 overflow-y-auto">
            {filteredDistricts.length > 0 ? (
              filteredDistricts.map((districtName) => (
                <button
                  key={districtName}
                  onClick={() => handleSelect(districtName)}
                  className="w-full px-4 py-2.5 text-left hover:bg-[#B1336E]/5 transition-colors duration-150 flex items-center justify-between group"
                >
                  <span className="text-sm text-slate-900 font-montserrat group-hover:text-[#B1336E]">
                    {districtName}
                  </span>
                  {district === districtName && (
                    <Check className="w-4 h-4 text-[#B1336E]" />
                  )}
                </button>
              ))
            ) : (
              <div className="px-4 py-3 text-sm text-slate-500 text-center font-montserrat">
                No se encontraron distritos
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DistrictSelect;